import { useState } from 'react';
import type { ReactNode } from 'react';
import { T, AppHeader } from '../../design';
import type { ToastData } from '../../design';
import { usePortal } from '../../context/PortalContext';
import type { Screen, NavStack } from '../../portal/navigation';
import type { Claim, ClaimActor, ClaimDraft, DraftItem } from '../../types/claims.types';
import { useClaims, useClaimSubmit, useProductSearch, useClaimLookups } from '../../hooks/claims';
import { parseClaimFieldErrors } from '../../utils/claimErrors';
import type { ItemFieldErrors } from '../../utils/claimErrors';
import { ClaimsList } from './screens/ClaimsList';
import { ClaimCreate } from './screens/ClaimCreate';
import { ClaimItemEditor } from './screens/ClaimItemEditor';
import { ClaimDetail } from './screens/ClaimDetail';

export interface ClaimsModuleProps {
  screen: Screen;
  nav: NavStack;
  fireToast: (toast: ToastData) => void;
}

const MAIN_PAD = '18px 14px calc(36px + env(safe-area-inset-bottom))';
const EMPTY_DRAFT: ClaimDraft = { preferredOutcome: null, items: [] };

export function ClaimsModule({ screen, nav, fireToast }: ClaimsModuleProps) {
  const portal = usePortal();
  const salonEmployeeId = portal.employee?.id ?? null;

  // En intern bruger opretter claims som den medarbejder, de agerer som (impersonation).
  const actor: ClaimActor | null = salonEmployeeId != null ? { employeeId: salonEmployeeId } : null;
  const isAuthenticated = portal.isAuthenticated && actor != null;

  const claims = useClaims(actor, isAuthenticated);
  const submit = useClaimSubmit(actor);
  const search = useProductSearch(actor);
  const lookups = useClaimLookups(actor, false);

  const [draft, setDraft] = useState<ClaimDraft>(EMPTY_DRAFT);
  const [serverErrors, setServerErrors] = useState<Record<number, ItemFieldErrors>>({});

  function startNew() {
    setDraft(EMPTY_DRAFT);
    setServerErrors({});
    nav.push({ type: 'claims-create' });
  }

  function saveItem(item: DraftItem) {
    const idx = typeof screen.index === 'number' ? screen.index : null;
    setDraft((s) => {
      const items = [...s.items];
      if (idx != null) items[idx] = item; else items.push(item);
      return { ...s, items };
    });
    nav.back();
  }

  function removeItem(index: number) {
    setDraft((s) => ({ ...s, items: s.items.filter((_, i) => i !== index) }));
  }

  async function handleSubmit() {
    const r = await submit.submit(draft);
    if (r.success) {
      fireToast({ tone: 'success', msg: 'Reklamation oprettet' });
      setDraft(EMPTY_DRAFT);
      setServerErrors({});
      nav.replaceStack([{ type: 'claims-list' }]);
      claims.refresh();
      return;
    }
    if (r.fieldErrors) {
      const parsed = parseClaimFieldErrors(r.fieldErrors);
      setServerErrors(parsed.items);
      fireToast({ tone: 'error', msg: parsed.top[0] ?? 'Ret de markerede felter og prøv igen.' });
      if (parsed.firstItemIndex != null) nav.push({ type: 'claims-item', index: parsed.firstItemIndex });
      return;
    }
    fireToast({ tone: 'error', msg: r.error ?? 'Kunne ikke oprette reklamationen.' });
  }

  // ---- Routing ----
  let title = 'Reklamationer';
  let body: ReactNode = null;
  let eyebrow = 'Reklamationer';

  if (screen.type === 'claims-create') {
    title = 'Ny reklamation';
    body = <ClaimCreate draft={draft} setDraft={setDraft} onAddItem={() => { setServerErrors({}); nav.push({ type: 'claims-item' }); }} onEditItem={(i) => nav.push({ type: 'claims-item', index: i })} onRemoveItem={removeItem} onSubmit={handleSubmit} submitting={submit.isSubmitting} />;
  } else if (screen.type === 'claims-item') {
    const idx = typeof screen.index === 'number' ? screen.index : null;
    title = idx != null ? 'Rediger vare' : 'Tilføj vare';
    body = <ClaimItemEditor key={`claims-item-${idx ?? 'new'}`} initial={idx != null ? draft.items[idx] : null} draft={draft} serverErrors={idx != null ? (serverErrors[idx] ?? {}) : {}} search={search} lookupOrder={lookups.lookupOrder} searchCustomer={lookups.searchCustomer} onSave={saveItem} onCancel={nav.back} toast={fireToast} />;
  } else if (screen.type === 'claims-detail') {
    const claim = screen.claim as Claim;
    title = `#${claim.id}`;
    body = <ClaimDetail claim={claim} />;
  } else {
    const salonName = portal.salon?.name;
    if (salonName) eyebrow = salonName;
    body = <ClaimsList claims={claims.claims} loading={claims.isLoading} error={claims.error} onRetry={claims.refresh} onOpen={(c) => nav.push({ type: 'claims-detail', claim: c })} onNew={startNew} />;
  }

  return (
    <>
      <AppHeader eyebrow={eyebrow} title={title} onBack={nav.back} />
      <main style={{ flex: 1, padding: MAIN_PAD, background: T.bg }}>{body}</main>
    </>
  );
}
